/**
 * Period Timeline (Epic 3 — Toewijzing met tijdspercentages en tijdvakken,
 * Story 3.3)
 *
 * "Laag 2" van de UX-spec (S3): collapsible tijdlijn onder de "Actueel
 * pakket"-kaart (Story 3.2), default dicht. Puur een read-only projectie
 * van wat `assignmentService` al bewaakt (AD-2) — geen eigen validatielaag.
 *
 * Dit project heeft geen shadcn/ui (zie `CoreTasksTab.tsx` Dev Notes) —
 * "Collapsible" is een native `<details>`/`<summary>` (zelfde patroon als
 * de vaardigheden-uitklap in `CurrentPackageCard.tsx`), geen Material
 * Symbols Outlined-iconenset geïnstalleerd (nergens elders in dit project
 * gebruikt) — het slotje is een inline SVG, consistent met de overige
 * inline-SVG-iconen in `EmployeeDetailPage.tsx`. Tooltip is `HoverTooltip`.
 */
import { HoverTooltip } from '../../../components/HoverTooltip';
import { formatDutchDate, formatPeriodRange } from '../../../lib/dateUtils';
import type { AssignmentPeriodDTO } from '@functieloonhuis/shared';

interface PeriodTimelineProps {
  /** Alle tijdvakken van een medewerker (open + gesloten), al nieuwste-eerst gesorteerd (backend). */
  periods: AssignmentPeriodDTO[];
}

/**
 * Compacte pakket-samenvatting, bv. "Productfotografie 40 · Foto-intake 30".
 * De UX-spec-voorbeeldtekst gebruikt korte codes ("K2 40 · K17 30") maar
 * `core_tasks` heeft geen code-kolom (geverifieerd: alleen
 * naam/domein/is_active, Epic 2) — conform de story dev-notes valt dit
 * terug op de volledige kerntaaknaam.
 */
function formatPackageSummary(period: AssignmentPeriodDTO): string {
  return period.assignments.map((a) => `${a.coreTaskName} ${a.timePercentage}`).join(' · ');
}

function LockIcon() {
  return (
    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 10-8 0v4h8z"
      />
    </svg>
  );
}

export function PeriodTimeline({ periods }: PeriodTimelineProps) {
  return (
    <details className="mt-4 bg-white border border-[#E2E8F0] rounded-lg" data-testid="period-timeline">
      <summary className="px-6 py-4 text-sm font-medium text-gray-900 cursor-pointer select-none">
        Historie &amp; tijdvakken ({periods.length})
      </summary>
      <div className="px-6 pb-4">
        {periods.length === 0 ? (
          <p className="text-sm text-gray-500">Nog geen tijdvakken.</p>
        ) : (
          <ol className="relative border-l border-[#E2E8F0] ml-2 space-y-4" data-testid="period-timeline-list">
            {periods.map((period) => (
              <li key={period.id} className="relative ml-4" data-testid={`period-timeline-item-${period.id}`}>
                <div className="absolute -ml-[calc(1rem+4.5px)] mt-1.5 w-2 h-2 rounded-full bg-[#54949E]" aria-hidden="true" />
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">{formatPeriodRange(period.startDate, period.endDate)}</p>
                  {period.frozen && (
                    <HoverTooltip
                      testId={`period-frozen-tooltip-${period.id}`}
                      content={
                        period.frozenAt
                          ? `Vastgelegd in beoordeling van ${formatDutchDate(period.frozenAt)}; niet meer wijzigbaar`
                          : 'Vastgelegd in een definitieve beoordeling; niet meer wijzigbaar'
                      }
                    >
                      <span data-testid={`period-frozen-icon-${period.id}`}>
                        <LockIcon />
                      </span>
                    </HoverTooltip>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{formatPackageSummary(period)}</p>
              </li>
            ))}
          </ol>
        )}
      </div>
    </details>
  );
}
